import React from 'react';
import PropTypes from 'prop-types';

const RightColumnContext = React.createContext(undefined);

// Actions
const CLEAR_RIGHT_COLUMN_STATE = 'clear-right-column-state';
const START_LOADING_RIGHT_COLUMN_COLLECTION = 'start-loading-right-column-collection';
const FAILED_LOADING_RIGHT_COLUMN_COLLECTION = 'failed-loading-right-column-collection';
const SET_RIGHT_COLUMN_COLLECTION = 'set-right-column-collection';

const init = () => {
  return {
    rightColumnCollectionLoading: null,
    rightColumnCollectionError: null,
    rightColumnCollection: null,
  };
};

const reducer = (state, action) => {
  switch (action.type) {
    case CLEAR_RIGHT_COLUMN_STATE: {
      return init();
    }
    case START_LOADING_RIGHT_COLUMN_COLLECTION: {
      return {
        ...state,
        rightColumnCollectionLoading: true,
      };
    }
    case FAILED_LOADING_RIGHT_COLUMN_COLLECTION: {
      return {
        ...state,
        rightColumnCollectionError: action.rightColumnCollectionError,
      };
    }
    case SET_RIGHT_COLUMN_COLLECTION: {
      return {
        ...state,
        rightColumnCollectionLoading: null,
        rightColumnCollectionError: null,
        rightColumnCollection: action.rightColumnCollection,
      };
    }
    default: {
      throw new Error(`Unhandled RightColumnContext action type: ${action.type}`);
    }
  }
};

const RightColumnProvider = ({
  children,
}) => {
  const mountedRef = React.useRef(null);
  const [state, dispatch] = React.useReducer(reducer, null, init);

  const dispatchIfMounted = (args) => {
    if (mountedRef.current === true) {
      return dispatch(args);
    }

    return undefined;
  };

  const clearRightColumnCollection = () => {
    mountedRef.current = false;
    dispatch({ type: CLEAR_RIGHT_COLUMN_STATE });
  };

  const loadRightColumnCollectionLink = async (link) => {
    mountedRef.current = true;
    dispatch({ type: START_LOADING_RIGHT_COLUMN_COLLECTION });
    try {
      const resp = await link.get();
      dispatchIfMounted({ type: SET_RIGHT_COLUMN_COLLECTION, rightColumnCollection: resp });
    } catch (err) {
      dispatchIfMounted({ type: FAILED_LOADING_RIGHT_COLUMN_COLLECTION, rightColumnCollectionError: err });
    }
  };

  const memoizedValue = React.useMemo(() => {
    return {
      showRightColumnCollection: state.rightColumnCollection || state.rightColumnCollectionLoading || state.rightColumnCollectionError,
      rightColumnCollection: state.rightColumnCollection,
      rightColumnCollectionError: state.rightColumnCollectionError,
      rightColumnCollectionLoading: state.rightColumnCollectionLoading,
      loadRightColumnCollectionLink,
      clearRightColumnCollection,
    };
  }, [
    state,
    loadRightColumnCollectionLink,
    clearRightColumnCollection,
  ]);

  return (
    <RightColumnContext.Provider value={memoizedValue}>
      {children}
    </RightColumnContext.Provider>
  );
};

// Use RightColumn State Context
const useRightColumnContext = () => {
  const context = React.useContext(RightColumnContext);
  if (context === undefined) {
    throw new Error('useRightColumnContext must be used within a RightColumnProvider');
  }
  return context;
};

RightColumnProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  RightColumnProvider,
  useRightColumnContext,
};
